import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged } from 'rxjs';

/**
 * Single source of truth for the app shell's responsive state and the
 * sidebar drawer/rail open/closed state.
 *
 * Why a service:
 *   The previous design split this state across AppLayoutComponent
 *   (sidebarOpen / tabletRailExpanded), SidebarComponent (isOpen @Input,
 *   tabletExpanded @Input, isMobile), and a custom `hub:open-sidebar`
 *   document event. Three feedback paths (down via @Input, up via @Output,
 *   across via DOM event) plus per-component viewport checks meant the
 *   state could disagree across components mid-interaction — visible on
 *   iOS Safari, where every URL/toolbar collapse fires `resize` and every
 *   `<a>`-with-tooltip tap fires a focus event before the click.
 *
 * Design:
 *   - Viewport breakpoints driven by `matchMedia('change')` (only fires on
 *     actual breakpoint crossings — no iOS toolbar noise).
 *   - sidebarOpen$ and tabletRailExpanded$ are BehaviorSubjects; consumers
 *     read via `async` pipe.
 *   - One API for toggle/open/close; the service decides whether to act on
 *     the mobile drawer or the iPad rail based on current breakpoint.
 *   - No `@Input` / `@Output` plumbing between layout components.
 */
@Injectable({ providedIn: 'root' })
export class LayoutService {
  /** Mobile drawer (≤768px). Closed by default; the hamburger opens it. */
  private readonly _sidebarOpen = new BehaviorSubject<boolean>(false);
  /** iPad rail expansion (769–1199px). The 64px rail is always visible;
   *  the hamburger flips it to a 250px floating panel. */
  private readonly _tabletRailExpanded = new BehaviorSubject<boolean>(false);

  /** Listen to breakpoint changes via matchMedia (not `resize`). */
  private readonly _mobileMql = window.matchMedia('(max-width: 768px)'); // NOSONAR S7764 — browser-only Angular code
  private readonly _tabletMql = window.matchMedia('(min-width: 769px) and (max-width: 1199px)'); // NOSONAR S7764 — browser-only Angular code

  /** Public state — components subscribe via async pipe. */
  readonly sidebarOpen$: Observable<boolean> = this._sidebarOpen.pipe(distinctUntilChanged());
  readonly tabletRailExpanded$: Observable<boolean> =
    this._tabletRailExpanded.pipe(distinctUntilChanged());

  // SCRUM-1962 — detected once at startup from the User-Agent. The current
  // Tesla in-car browser is Chromium with a `Tesla/<ver>` token; the legacy
  // Model S/X browser was `QtCarBrowser`. Heuristic only (UA is spoofable and
  // Tesla can change it) — it only drives presentation, never security.
  private readonly _isTesla =
    // SCRUM-2296 — main.ts is the single detection authority: it applies the
    // `tesla` class pre-boot from UA OR the persisted switch OR the viewport
    // heuristic. Read that class first so the layout logic (desktop sidebar,
    // two-column) follows the SAME decision, instead of re-deriving a narrower
    // one here (previously this missed the heuristic path entirely).
    (typeof document !== 'undefined' && document.documentElement.classList.contains('tesla')) ||
    (typeof navigator !== 'undefined' && /\bTesla\b|QtCarBrowser/i.test(navigator.userAgent)) ||
    // Honor the persisted manual switch (set via ?carmode=1) so large-car-text
    // mode works even on cars whose browser UA doesn't advertise "Tesla".
    (typeof localStorage !== 'undefined' && localStorage.getItem('hub_car_mode') === '1');

  /** True when running in a Tesla in-car browser (best-effort UA detection).
   *  The `tesla` root class is applied pre-boot in main.ts; this getter lets
   *  components branch on it in logic. */
  get isTesla(): boolean { return this._isTesla; }

  constructor() {
    // Initial state: desktop opens the sidebar; mobile starts closed; iPad
    // shows its 64px rail unconditionally so sidebarOpen is also true there.
    this._sidebarOpen.next(!this.isMobile);

    // Auto-close mobile drawer when crossing OUT of mobile→desktop or vice
    // versa. matchMedia fires only on actual breakpoint crossings, so this
    // ignores the iOS Safari address-bar collapse resize storm.
    this._mobileMql.addEventListener('change', (e) => this.onMobileChange(e.matches));
    this._tabletMql.addEventListener('change', (e) => this.onTabletChange(e.matches));
  }

  // SCRUM-2285 — a Tesla in-car screen (~1088px) falls inside the tablet band,
  // which otherwise gives it the 64px icon-only rail. Treat the car as desktop
  // so it gets the full labeled sidebar and desktop toggle behaviour. Real
  // (non-Tesla) tablets/iPads at the same width are unaffected.
  get isMobile(): boolean { return this._mobileMql.matches && !this.isTesla; }
  get isTablet(): boolean { return this._tabletMql.matches && !this.isTesla; }
  get sidebarOpen(): boolean { return this._sidebarOpen.value; }
  get tabletRailExpanded(): boolean { return this._tabletRailExpanded.value; }

  /**
   * The hamburger button's only action. Behaviour by breakpoint:
   *   - Mobile (≤768px): toggle the drawer.
   *   - Tablet (769–1199px): flip the rail between 64px icon-only and 250px
   *     expanded; sidebarOpen stays true so the rail is always visible.
   *   - Desktop (≥1200px): toggle the persistent sidebar.
   */
  toggleSidebar(): void {
    if (this.isTablet) {
      const next = !this._tabletRailExpanded.value;
      this.dbg(`toggle tablet rail ${this._tabletRailExpanded.value}→${next}`);
      this._tabletRailExpanded.next(next);
    } else {
      const next = !this._sidebarOpen.value;
      this.dbg(`toggle sidebar ${this._sidebarOpen.value}→${next} mob=${this.isMobile} tab=${this.isTablet}`);
      this._sidebarOpen.next(next);
    }
  }

  /** Force the drawer closed. Used by menu-item taps on mobile/tablet. */
  closeSidebar(): void {
    this.dbg(`closeSidebar mob=${this.isMobile} tab=${this.isTablet} open=${this._sidebarOpen.value} rail=${this._tabletRailExpanded.value}`);
    if (this.isMobile && this._sidebarOpen.value) {
      this._sidebarOpen.next(false);
    }
    if (this.isTablet && this._tabletRailExpanded.value) {
      this._tabletRailExpanded.next(false);
    }
  }

  // ── TEMP iPhone diagnostics — written into the on-screen overlay ──
  /** @internal — toggled by AppLayoutComponent when ?debug=1 */
  _dbgEnabled = false;
  /** @internal — shared array with AppLayoutComponent's debugLines */
  _dbgLines: string[] = [];
  dbg(msg: string): void {
    const stamp = new Date().toISOString().slice(11, 23);
    const line = `${stamp} ${msg}`;
    console.log('[layout]', line);
    if (this._dbgEnabled) {
      this._dbgLines.push(line);
      if (this._dbgLines.length > 50) this._dbgLines.shift();
    }
  }

  /**
   * Close the drawer iff we're on mobile or the iPad rail is expanded.
   * Called from menu-item click handlers so a tap on a route both
   * navigates and dismisses the overlay drawer in one go.
   */
  closeIfOverlay(): void {
    this.closeSidebar();
  }

  private onMobileChange(isMobile: boolean): void {
    if (isMobile) { // NOSONAR S2301 — intentional pattern preserved
      // Crossed into mobile — start with the drawer closed.
      this._sidebarOpen.next(false);
      this._tabletRailExpanded.next(false);
    } else {
      // Crossed out of mobile — restore default open sidebar.
      this._sidebarOpen.next(true);
    }
  }

  private onTabletChange(isTablet: boolean): void {
    if (!isTablet) {
      // Leaving tablet — collapse the rail back to default.
      this._tabletRailExpanded.next(false);
    }
  }
}
